// Die Dachplanung mit K2 im Büro (seit dem Augentest E3, 21.10.2026 – Gernot
// hat sie in der Freigabe „fast nicht gefunden“):
//  - in der Freigabe eine eigene, deutliche Karte an festem Platz, gleich
//    unter der Kopf-Karte, vor den Fragen (Aufnahme.tsx);
//  - überall, wo sie liegt (Freigabe, Akte), zwei Knöpfe: „Ansehen“ zeigt
//    das PDF im Browser in einem neuen Tab (der Server schickt es „inline“),
//    „Herunterladen“ speichert es mit lesbarem Namen (dachplanung.ts).
//    „Ansehen“ hieß bis 23.10.2026 „Öffnen“: Gernot hat das PDF von dort aus
//    über „Drucken → als PDF“ gesichert und bekam die Prüfsumme als Namen.
//    Das Wort sagt jetzt, wofür der Knopf ist; gesichert wird mit
//    „Herunterladen“.
//  - seit E3c (S3) darunter der Excel-Report von K2 (die
//    Unterkonstruktionsliste für den Lieferanten): herunterladen, und wer die
//    Akte pflegt (Leitung, Büro), hängt ihn hier an oder ersetzt ihn.

import { darf, PRODUKTLINIEN, type CrmAufnahme } from "@happy-energy/kern";
import { Chip, Ikon } from "@happy-energy/oberflaeche";
import { k2ExcelDateiname } from "../ablauf/ablauf.ts";
import { K2ExcelKnopf } from "../ablauf/gemeinsam.tsx";
import { dateiAdresse, groesseZeigen, heuteInWien, tagZeigen } from "../anzeige.ts";
import { useCrm } from "../zusammenhang.tsx";
import { dachplanungDateiname, type DateiRahmen } from "./dachplanung.ts";

/**
 * Vorne ein Tag (in Wien) – bei Dachplanung und Unterlage der Tag der
 * Besichtigung –, hinten die Produktlinie: für die Namen gespeicherter
 * Dateien (seit E3b, O76). Ohne Produktlinie: ohne Produkt.
 */
export function dateiRahmen(zeitpunkt: string | null | undefined, produktlinie: string | undefined): DateiRahmen {
  const produkt = produktlinie === undefined ? null : (PRODUKTLINIEN.find((p) => p.schluessel === produktlinie)?.name ?? "Happy Energy");
  const zeit = zeitpunkt ? new Date(zeitpunkt) : null;
  return { tag: zeit && !Number.isNaN(zeit.getTime()) ? heuteInWien(zeit) : null, produkt };
}

/**
 * „Ansehen“ und „Herunterladen“. Der Kundenname geht nur in den Namen der
 * Datei, nie in die Adresse. `haupt`: „Ansehen“ als dunkler Hauptknopf.
 */
export function DachplanungKnoepfe({
  pruefsumme,
  kundeName,
  rahmen,
  haupt = false,
}: {
  pruefsumme: string;
  kundeName: string;
  /** Seit E3b: Datum vorne, Produkt hinten (O76). */
  rahmen?: DateiRahmen;
  haupt?: boolean;
}) {
  const adresse = dateiAdresse(pruefsumme);
  const dateiname = dachplanungDateiname(kundeName, rahmen);
  return (
    <>
      <a className={`crm-knopf ${haupt ? "dunkel" : "rand"} klein`} href={adresse} target="_blank" rel="noopener noreferrer" type="application/pdf"
        title="Zeigt das PDF – zum Speichern „Herunterladen“"
      >
        <Ikon name="auge" groesse="k" /> Ansehen
      </a>
      <a className="crm-knopf rand klein" href={adresse} download={dateiname} type="application/pdf" title={`Speichert „${dateiname}“`}>
        Herunterladen
      </a>
    </>
  );
}

/** Die Karte in der Freigabe: PDF-Zeichen, „Dachplanung mit K2“, Größe, „geändert am …“, Ansehen und Herunterladen – seit E3c darunter die K2-Liste (Excel). */
export function DachplanungKarte({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  const { ich } = useCrm();
  const k2 = a.anlage.k2;
  const excel = a.anlage.k2Excel ?? null;
  const pruefsumme = k2.pruefsumme;
  // „geändert am …“ nur, wenn es nach dem Senden war – vorher ist es einfach die Dachplanung.
  const geaendert = a.anlage.k2GeaendertAm && a.gesendetAm && a.anlage.k2GeaendertAm > a.gesendetAm ? a.anlage.k2GeaendertAm : null;
  return (
    <section className={`crm-karte k2-zeile${pruefsumme !== null ? "" : " ohne"}`} aria-label="Dachplanung mit K2">
      <span className="avatar avatar-pdf" aria-hidden="true">
        PDF
      </span>
      <div className="k2-wort">
        <b>Dachplanung mit K2</b>
        <span>
          {pruefsumme !== null
            ? `K2-PDF samt Stückliste${k2.groesse !== null ? ` · ${groesseZeigen(k2.groesse)}` : ""}`
            : k2.uebersprungen
              ? "Übersprungen – das PDF kann jederzeit nachkommen."
              : "Noch keine – das PDF kann jederzeit nachkommen."}
        </span>
      </div>
      <div className="k2-rechts">
        {geaendert && <Chip art="warn">geändert am {tagZeigen(geaendert)}</Chip>}
        {pruefsumme !== null ? (
          <DachplanungKnoepfe pruefsumme={pruefsumme} kundeName={a.kunde.name} rahmen={dateiRahmen(a.begonnenAm, a.anlage.produktlinie)} haupt />
        ) : (
          <Chip {...(k2.uebersprungen ? {} : { ikon: "uhr" as const })}>{k2.uebersprungen ? "übersprungen" : "offen"}</Chip>
        )}
      </div>
      <div className="k2-excel">
        <span className="leise klein">K2-Liste (Excel) für die Bestellung: {excel ? "abgelegt" : "noch keine"}</span>
        {excel && (
          <a className="crm-knopf rand klein" href={dateiAdresse(excel)} download={k2ExcelDateiname(a.kunde.name, dateiRahmen(a.begonnenAm, a.anlage.produktlinie))}>
            Herunterladen
          </a>
        )}
        {darf(ich.nutzer.rolle, "kunden") && <K2ExcelKnopf anlageId={a.anlage.id} da={excel !== null} fertig={neuLaden} />}
      </div>
    </section>
  );
}
